import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Form, Image, Modal, Skeleton, Spin } from "antd";
import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormContentBlocker, FormContentBlockerLayout, useFormContentBlockerHandler, useFormContentBlockerProvider } from "@devowl-wp/react-cookie-banner-admin";
import { scrollTo } from "@devowl-wp/react-utils";
import { BlockerFormNoticeNoneExistingCookies } from "./noticeNoneExistingCookies.js";
import { BlockerFormNoticeNoneExistingTcfVendors } from "./noticeNoneExistingTcfVendors.js";
import { useRouteBlocker } from "../../../hooks/useRouteBlocker.js";
import { BlockerModel } from "../../../models/blockerModel.js";
import { useStores } from "../../../store/stores.js";
import { __ } from "../../../utils/i18n.js";
import { MediaLibrarySelector } from "../../mediaLibrarySelector.js";
import { PostTypeSelect } from "../../postTypeSelect.js";
import { ConfigContent } from "../content.js";
import { CookieEditForm } from "../cookies/form.js";
const BlockerEditForm = observer(({ template, overwriteAttributes, navigateAfterCreation = true, comingFromServiceCreation = false })=>{
    const { blocker, id, queried, fetched, link } = useRouteBlocker();
    const navigate = useNavigate();
    const [isCreatingNewCookie, setIsCreatingNewCookie] = useState(false);
    const { cookieStore, tcfStore, optionStore: { isTcf, others: { isPro } } } = useStores();
    const templateModel = blocker?.templateModel || cookieStore.templatesBlocker.get(template?.identifier);
    const { declarations } = tcfStore;
    const { prompt, form, isBusy, defaultValues, nonExistingServices, nonExistingTcfVendors, onFinish, onFinishFailed, onValuesChange, contextValue } = useFormContentBlockerHandler({
        attributes: overwriteAttributes,
        isTcf,
        isEdit: fetched,
        entityTemplateVersion: blocker?.data?.meta?.presetVersion,
        template: templateModel?.use,
        initialHasChanges: comingFromServiceCreation,
        handleSave: async (values)=>{
            try {
                const { name, status, description, succeessorDeletionCheck, ...meta } = values;
                const newMeta = {
                    ...meta,
                    visualMediaThumbnail: meta.visualMediaThumbnail || 0,
                    criteria: meta.criteria,
                    tcfVendors: meta.tcfVendors?.join(",") || "",
                    tcfPurposes: meta.tcfPurposes?.join(",") || "",
                    services: meta.services?.join(",") || "",
                    isVisual: meta.criteria === "services" ? meta.isVisual : false,
                    presetId: templateModel?.data.identifier,
                    presetVersion: templateModel?.data.version
                };
                delete newMeta.templateCheck;
                if (queried) {
                    blocker.setName(name);
                    blocker.setStatus(status);
                    blocker.setDescription(description);
                    blocker.setMeta(newMeta);
                    await blocker.patch();
                } else {
                    const draft = new BlockerModel(blockers, {
                        title: {
                            raw: name
                        },
                        content: {
                            raw: description,
                            protected: false
                        },
                        status,
                        meta: newMeta
                    });
                    await draft.persist();
                    // Delete successor-relevant services
                    if (succeessorDeletionCheck) {
                        const successorOfIds = templateModel.data.consumerData.successorOf.map(({ id })=>id);
                        const allBlockerModels = [
                            ...cookieStore.blockers.entries.values()
                        ];
                        const deleteModels = successorOfIds.map((id)=>allBlockerModels.find(({ key })=>key === id) || new BlockerModel(cookieStore.blockers, {
                                id
                            }));
                        await Promise.allSettled(deleteModels.map((m)=>m.delete()));
                    }
                }
                return ()=>navigateAfterCreation && (typeof navigateAfterCreation === "string" ? window.location.href = navigateAfterCreation : navigate(link.slice(1)));
            } catch (e) {
                throw e.responseJSON.message;
            }
        }
    });
    const notReady = queried && !fetched || templateModel && !templateModel.use;
    const [FormContentBlockerProvider, formContentBlockerContextValue] = useFormContentBlockerProvider({
        ...contextValue,
        tcfPurposes: Object.values(declarations?.purposes || [])
    }, {}, {
        deps: [
            notReady
        ]
    });
    const { blockers, essentialGroup } = cookieStore;
    const initialValues = fetched ? {
        name: blocker.data.title.raw,
        status: blocker.data.status,
        description: blocker.data.content.raw,
        criteria: blocker.data.meta.criteria,
        rules: blocker.data.meta.rules,
        tcfVendors: blocker.tcfVendors,
        tcfPurposes: blocker.tcfPurposes,
        services: blocker.services,
        isVisual: blocker.data.meta.isVisual,
        visualType: isPro ? blocker.data.meta.visualType : "default",
        visualMediaThumbnail: blocker.data.meta.visualMediaThumbnail,
        visualContentType: blocker.data.meta.visualContentType,
        isVisualDarkMode: blocker.data.meta.isVisualDarkMode,
        visualBlur: blocker.data.meta.visualBlur,
        visualDownloadThumbnail: blocker.data.meta.visualDownloadThumbnail,
        visualHeroButtonText: blocker.data.meta.visualHeroButtonText,
        shouldForceToShowVisual: blocker.data.meta.shouldForceToShowVisual,
        templateCheck: undefined,
        succeessorDeletionCheck: undefined
    } : defaultValues;
    // Automatically add created items to the dropdown
    const handleCreatedService = useCallback((model)=>{
        form.setFieldsValue({
            services: [
                ...form.getFieldValue("services"),
                model.key
            ]
        });
    }, [
        form
    ]);
    // Automatically add created items to the dropdown
    const handleCreatedTcfVendor = useCallback((model)=>{
        form.setFieldsValue({
            tcfVendors: [
                ...form.getFieldValue("tcfVendors"),
                model.key
            ]
        });
    }, [
        form
    ]);
    // Initially load the blocker if not yet done
    useEffect(()=>{
        if (queried && !fetched) {
            blockers.getSingle({
                params: {
                    id,
                    context: "edit"
                }
            });
        }
    }, [
        queried,
        fetched
    ]);
    useEffect(()=>{
        if (templateModel && !templateModel.use && !templateModel.busy) {
            templateModel.fetchUse();
        }
    }, [
        templateModel
    ]);
    // Scroll to top when opening the form
    useEffect(()=>{
        scrollTo(0);
        // Ensure the groups are loaded
        if (!essentialGroup) {
            cookieStore.fetchGroups();
        }
    }, []);
    useEffect(()=>{
        // Ensure the TCF declarations are loaded
        if (!declarations && isTcf) {
            tcfStore.fetchDeclarations();
        }
    }, [
        isTcf,
        declarations
    ]);
    if (notReady) {
        return /*#__PURE__*/ _jsx(ConfigContent, {
            maxWidth: "fixed",
            children: /*#__PURE__*/ _jsx(Skeleton, {
                active: true,
                paragraph: {
                    rows: 8
                }
            })
        });
    }
    return /*#__PURE__*/ _jsx(ConfigContent, {
        maxWidth: "fixed",
        children: /*#__PURE__*/ _jsx(FormContentBlockerProvider, {
            value: formContentBlockerContextValue,
            children: /*#__PURE__*/ _jsxs(Spin, {
                spinning: isBusy || templateModel?.busy || false,
                children: [
                    prompt,
                    /*#__PURE__*/ _jsx(Form, {
                        name: `blocker-${id}`,
                        form: form,
                        ...FormContentBlockerLayout,
                        initialValues: initialValues,
                        onFinish: onFinish,
                        onFinishFailed: onFinishFailed,
                        onValuesChange: onValuesChange,
                        scrollToFirstError: {
                            behavior: "smooth",
                            block: "center"
                        },
                        labelWrap: true,
                        children: /*#__PURE__*/ _jsx(FormContentBlocker, {
                            technicalProps: {
                                servicesFieldProps: {
                                    dropdown: /*#__PURE__*/ _jsx(PostTypeSelect, {
                                        postType: [
                                            "rcb-cookie"
                                        ],
                                        multiple: true,
                                        filter: (d)=>d["rcb-cookie-group"][0] !== essentialGroup?.key
                                    }),
                                    children: /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx("button", {
                                                type: "button",
                                                className: "button",
                                                onClick: ()=>setIsCreatingNewCookie(true),
                                                children: __("Create new service")
                                            }),
                                            /*#__PURE__*/ _jsx(Modal, {
                                                open: isCreatingNewCookie,
                                                title: __("Add service"),
                                                width: "calc(100% - 50px)",
                                                bodyStyle: {
                                                    paddingBottom: 0
                                                },
                                                footer: null,
                                                onCancel: ()=>setIsCreatingNewCookie(false),
                                                children: /*#__PURE__*/ _jsx(CookieEditForm, {
                                                    navigateAfterCreation: false,
                                                    scrollToTop: false,
                                                    onCreated: (model)=>{
                                                        setIsCreatingNewCookie(false);
                                                        handleCreatedService(model);
                                                    }
                                                })
                                            }, id),
                                            /*#__PURE__*/ _jsx(BlockerFormNoticeNoneExistingCookies, {
                                                nonExistingServices: nonExistingServices,
                                                onCreated: handleCreatedService
                                            })
                                        ]
                                    })
                                },
                                tcfVendorsFieldProps: {
                                    dropdownVendors: /*#__PURE__*/ _jsx(PostTypeSelect, {
                                        postType: [
                                            "rcb-tcf-vendor-conf"
                                        ],
                                        multiple: true,
                                        titleRender: ({ vendor: { name } })=>name
                                    }),
                                    children: /*#__PURE__*/ _jsx(BlockerFormNoticeNoneExistingTcfVendors, {
                                        nonExistingTcfVendors: nonExistingTcfVendors,
                                        onCreated: handleCreatedTcfVendor
                                    })
                                }
                            },
                            visualProps: {
                                visualMediaThumbnailPicker: /*#__PURE__*/ _jsx(MediaLibrarySelector, {
                                    title: __("Select preview image"),
                                    allowedTypes: [
                                        "image"
                                    ],
                                    render: ({ open, reset, attachmentId, url })=>/*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsxs("p", {
                                                    style: {
                                                        marginTop: 0
                                                    },
                                                    children: [
                                                        /*#__PURE__*/ _jsx("a", {
                                                            className: "button",
                                                            onClick: open,
                                                            children: attachmentId ? __("Replace image") : __("Select from media library")
                                                        }),
                                                        "\xa0",
                                                        attachmentId && /*#__PURE__*/ _jsx("a", {
                                                            className: "button",
                                                            onClick: reset,
                                                            children: __("Remove image")
                                                        })
                                                    ]
                                                }),
                                                url && /*#__PURE__*/ _jsx(Image, {
                                                    width: 272,
                                                    src: url
                                                })
                                            ]
                                        })
                                })
                            }
                        })
                    })
                ]
            })
        })
    });
});
export { BlockerEditForm };
